
import { swalErrNotification } from "@/core/helpers/utils";
import { useButtonCustom } from "@/core/hooks/use-button-custom";
import { useDateTime } from "@/core/hooks/use-date-time-hook";
import {
  IDinhMucCoDinhNewResponseBase,
  IVatTuDinhMucCoDinh,
} from "@/core/interfaces/ApiResponses";
import { DinhMucCoDinhService } from "@/core/services/DinhMucCoDinh.service";
import { defineComponent, nextTick } from "vue";

import Pagination from "@/components/shared/pagination/Pagination.vue";
import { useLoading } from "@/core/hooks/use-loading";
import { usePagination } from "@/core/hooks/use-pagination";
import { VatTuService } from "@/core/services/VatTu.service";
import PageContentSection from "@/layout/_shared/PageContentSection.vue";
import ButtonCustom from "../shared/buttons/ButtonCustom.vue";
import DinhMucCoDinhVatTuTable from "./dinh-muc-co-dinh-table/DinhMucCoDinhVatTuTable.vue";

export default defineComponent({
  name: "dinh-muc-co-dinh-section",

  emits: ["on-done-loading"],

  components: {
    ButtonCustom,
    DinhMucCoDinhVatTuTable,
    PageContentSection,
    Pagination,
  },

  setup() {
    const { formatDisplayingDate } = useDateTime();
    const { startLoading, endLoading } = useLoading();

    const { ButtonTypeColors, ButtonsType } = useButtonCustom();

    const { currentPage, totalItems, perPage, perPageArr } = usePagination();

    return {
      ButtonTypeColors,
      ButtonsType,
      formatDisplayingDate,
      currentPage,
      totalItems,
      perPage,
      perPageArr,
      startLoading,
      endLoading,
    };
  },

  props: {
    showCreatePhieuXuatButton: {
      type: Boolean,
      default: true,
    },
    isButtonDisabled: {
      type: Boolean,
      default: false,
    },
    buttonTooltipMessage: {
      type: String,
      default: "",
    },
    idBienBan: {
      type: Number,
      default: 0,
    },
    idKho: {
      type: Number,
      default: 0,
    },
    idCapSuaChua: {
      type: Number,
      required: true,
    },
    idDanhMucToaXe: {
      type: Number,
      required: true,
    },
    statusFilter: {
      type: String,
      default: "",
    },
  },

  data() {
    return {
      dinhMucCoDinhDetail: null as null | IDinhMucCoDinhNewResponseBase,
      vatTuItems: [] as IVatTuDinhMucCoDinh[],
    };
  },

  mounted() {
    this.fetchData();
  },

  watch: {
    idCapSuaChua() {
      this.fetchData();
    },

    idDanhMucToaXe() {
      this.fetchData();
    },

    async perPage() {
      await nextTick();

      if (this.currentPage === 1) {
        this.fetchData();

        return;
      }

      this.currentPage = 1;
    },

    currentPage() {
      this.fetchData();
    },
  },

  methods: {
    async fetchData() {
      try {
        await this.startLoading();

        const dinhMucDetailRes =
          await DinhMucCoDinhService.getDinhMucCoDinhByIdCapSuaChuaAndDMTX({
            id_cap_sc: this.idCapSuaChua,
            id_dm_toa_xe: this.idDanhMucToaXe,
            ...(this.statusFilter && { status: this.statusFilter }),
          });

        if (dinhMucDetailRes) {
          const {
            data: {
              data: { data, total },
            },
          } = await VatTuService.getVatTuItemsByDinhMucCoDinhId({
            id_dinh_muc: dinhMucDetailRes.id ?? 0,
            current_page: this.currentPage,
            per_page: this.perPage,
          });

          this.totalItems = total;

          this.dinhMucCoDinhDetail = dinhMucDetailRes;

          this.vatTuItems = data.map((item) => ({
            ...item,
            pivot: {
              id_dinh_muc: dinhMucDetailRes.id ?? 0,
              id_vat_tu: item.id,
              so_luong: item.so_luong_dinh_muc,
            },
          }));
        }

        await this.endLoading();
      } catch (e) {
        this.$emit("on-done-loading", {
          data: null,
        });

        this.endLoading();

        await swalErrNotification(
          e,
          "Có lỗi xảy ra khi lấy thông tin định mức cố định"
        );
      } finally {
        this.$emit("on-done-loading", {
          data: this.dinhMucCoDinhDetail,
        });
      }
    },
  },
});
